import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, Layers, Sparkles, ArrowRight, BookOpen, CheckCircle, FileText } from 'lucide-react';

const CaseLawsSection = () => {
  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-t border-b border-cyan-100 relative">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 text-xs font-bold mb-3">
              <Sparkles className="w-3.5 h-3.5 text-cyan-600" />
              Autonomous AI Legal Intelligence
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              GST & Income Tax <span className="text-cyan-600">Case Laws</span>
            </h2>
            <p className="mt-2 text-base text-slate-600 max-w-2xl">
              Daily updated legal decisions from ITAT, Supreme Court, High Courts, and GST Authorities.
              Read verbatim court text alongside automated 1-minute AI summaries.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/case-laws/income-tax"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-800 hover:bg-cyan-50 hover:text-cyan-700 transition border border-slate-200"
            >
              <Scale className="w-4 h-4 text-cyan-600" />
              Income Tax Judgments
            </Link>
            <Link
              to="/case-laws/gst"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-800 hover:bg-teal-50 hover:text-teal-700 transition border border-slate-200"
            >
              <Layers className="w-4 h-4 text-teal-600" />
              GST Rulings
            </Link>
          </div>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {/* Card 1: Reassessment Reform */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all flex flex-col justify-between group">
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                  Income Tax • SC
                </span>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                  Revenue
                </span>
              </div>
              <div className="text-xs font-bold text-cyan-700 mb-1 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" />
                Civil Appeal No. 7349/2023
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-cyan-600 transition-colors">
                Union of India vs. Ashish Agarwal
              </h3>
              <p className="text-xs text-slate-600 line-clamp-3 mb-4 leading-relaxed">
                Landmark Supreme Court ruling directing all reassessment notices issued after 01.04.2021 under old section 148 to be deemed show-cause notices under new Section 148A(b).
              </p>
              <div className="flex flex-wrap gap-1 mb-4">
                <span className="text-[10px] font-bold bg-white border border-slate-200 text-slate-700 px-2 py-0.5 rounded">
                  Sec 148 / 148A
                </span>
                <span className="text-[10px] font-bold bg-white border border-slate-200 text-slate-700 px-2 py-0.5 rounded">
                  Finance Act 2021
                </span>
              </div>
            </div>
            <Link
              to="/case-laws"
              className="text-xs font-bold text-cyan-600 group-hover:text-cyan-700 flex items-center gap-1 pt-3 border-t border-slate-200"
            >
              Open Split-Screen View <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Card 2: Ocean Freight IGST */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all flex flex-col justify-between group">
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-teal-100 text-teal-800">
                  GST • Supreme Court
                </span>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                  ✓ Assessee
                </span>
              </div>
              <div className="text-xs font-bold text-teal-700 mb-1 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" />
                Civil Appeal No. 2520/2022
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                Union of India vs. Mohit Minerals Pvt. Ltd.
              </h3>
              <p className="text-xs text-slate-600 line-clamp-3 mb-4 leading-relaxed">
                Supreme Court ruled that reverse charge IGST cannot be levied on ocean freight for CIF imports since customs duty is already assessed on CIF value.
              </p>
              <div className="flex flex-wrap gap-1 mb-4">
                <span className="text-[10px] font-bold bg-white border border-slate-200 text-slate-700 px-2 py-0.5 rounded">
                  Sec 5(3) IGST
                </span>
                <span className="text-[10px] font-bold bg-white border border-slate-200 text-slate-700 px-2 py-0.5 rounded">
                  Composite Supply
                </span>
              </div>
            </div>
            <Link
              to="/case-laws"
              className="text-xs font-bold text-teal-600 group-hover:text-teal-700 flex items-center gap-1 pt-3 border-t border-slate-200"
            >
              Open Split-Screen View <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Card 3: Unexplained Cash Credit Sec 68 */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all flex flex-col justify-between group">
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-cyan-100 text-cyan-800">
                  Income Tax • ITAT Mumbai
                </span>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                  ✓ Assessee
                </span>
              </div>
              <div className="text-xs font-bold text-cyan-700 mb-1 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" />
                ITA No. 1285/MUM/2023
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-cyan-600 transition-colors">
                ACIT vs. Reliance Digital Media Ltd
              </h3>
              <p className="text-xs text-slate-600 line-clamp-3 mb-4 leading-relaxed">
                Additions under Section 68 cannot be sustained where identity and creditworthiness of investor are proven, and AO cannot arbitrarily discard DCF valuation method.
              </p>
              <div className="flex flex-wrap gap-1 mb-4">
                <span className="text-[10px] font-bold bg-white border border-slate-200 text-slate-700 px-2 py-0.5 rounded">
                  Section 68
                </span>
                <span className="text-[10px] font-bold bg-white border border-slate-200 text-slate-700 px-2 py-0.5 rounded">
                  Rule 11UA(2)
                </span>
              </div>
            </div>
            <Link
              to="/case-laws"
              className="text-xs font-bold text-cyan-600 group-hover:text-cyan-700 flex items-center gap-1 pt-3 border-t border-slate-200"
            >
              Open Split-Screen View <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="bg-gradient-to-r from-slate-900 via-cyan-950 to-slate-900 text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl border border-cyan-800/40">
          <div>
            <h4 className="text-lg sm:text-xl font-bold text-white mb-1">
              Search by Case ID, Party Name, Section, or Court
            </h4>
            <p className="text-xs sm:text-sm text-slate-300">
              Instant keyword search across thousands of Indian tax rulings with instant AI legal summaries.
            </p>
          </div>
          <Link
            to="/case-laws"
            className="whitespace-nowrap px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-400 text-slate-950 font-black text-xs sm:text-sm hover:from-cyan-400 hover:to-teal-300 transition-all shadow-md flex items-center gap-2"
          >
            Explore Case Law Hub
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CaseLawsSection;
